<template>
	<div class="resource-panel-configure-popup">
		<div class="resource-panel-preview">
			<div
				class="resource-preview"
				v-for="(resource, index) in [...tempResourcePanelConfig].slice(
					0,
					6,
				)"
				:key="index"
			>
				<div class="resource-order-number">{{ index + 1 }}</div>

				<div class="resource-icon-wrapper">
					<AppMainIcon
						v-if="resource.length > 0"
						class="sub-resource__icon"
						:iconName="resource"
						:iconSize="20"
					/>
					<div class="question-mark" v-else>?</div>
				</div>
			</div>
		</div>
		<HorizontalDecorDivider with-sides />
		<div class="resource-panel-option-header">
			{{ translate("ui.select_resources_order") }}
		</div>
		<ScrollFade :direction="ScrollDirection.VERTICAL">
			<template #scrollContent>
				<div class="resource-panel-options">
					<div
						class="resource-panel-option-line"
						v-for="(
							resource, index
						) in DEFAULT_RESOURCE_PANEL_CONFIG"
						:key="index"
						:class="
							isResourceInSelected(resource) ? 'selected' : ''
						"
					>
						<div class="resource-panel-option-line__label">
							<AppMainIcon
								v-if="resource.length > 0"
								class="sub-resource__icon"
								:iconName="resource"
								:iconSize="28"
							/>
							<ShrinkText
								class="label-text"
								:text="translate(`resource.${resource}`)"
								:fontSize="13"
							/>
							<span
								:class="
									isResourceInSelected(resource)
										? 'icon-check'
										: 'icon-red-x'
								"
							></span>
						</div>
						<SubResourceDivider />
						<div class="resource-panel-option-line__options">
							<div
								class="resource-panel-option-line__options__option"
								v-for="orderNumber in [1, 2, 3, 4, 5, 6]"
								:key="orderNumber"
								@click="changeOrder(resource, orderNumber)"
								:class="
									isResourceInThisOrderClass(
										resource,
										orderNumber,
									)
								"
							>
								{{ orderNumber }}
							</div>
						</div>
					</div>
				</div>
			</template>
		</ScrollFade>
		<HorizontalDecorDivider with-sides class="border-like" />
		<div class="error-message">
			<template v-if="isButtonDisabled()">
				{{
					translate("ui.resource_panel_configure_error").replace(
						"{number}",
						"6",
					)
				}}
			</template>
		</div>
		<AppTextButton
			class="resource-panel-configure-popup__btn"
			:type="StatusTypes.SUCCESS"
			:text="translate('button.confirm')"
			:disabled="isButtonDisabled()"
			@click="saveConfig()"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import SubResourceDivider from "@/components/SVG/SubResourceDivider.vue";
import { DEFAULT_RESOURCE_PANEL_CONFIG } from "@/game/config/resourcePanelConfig";
import type { ResourceType } from "@/game/entities/config/resources.ts";
import { usePopupStore } from "@/store/usePopupStore";
import useUserStore from "@/store/useUserStore";
import { ScrollDirection, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";

const { resourcePanelConfig } = storeToRefs(useUserStore());

const tempResourcePanelConfig = ref<(ResourceType | "")[]>([
	...resourcePanelConfig.value,
]);

onMounted(() => {
	// splice tempResourcePanelConfig to only have 6 elements, if it has more than 6
	if (tempResourcePanelConfig.value.length > 6) {
		tempResourcePanelConfig.value.splice(6);
	}
});

const changeOrder = (
	resource: ResourceType | "",
	orderNumber: number,
): void => {
	const newConfig = [...tempResourcePanelConfig.value];
	// if the resource is already at the desired position, place '' there instead, so that the user can choose another resource to place there
	if (newConfig[orderNumber - 1] === resource) {
		newConfig[orderNumber - 1] = "";
		tempResourcePanelConfig.value = newConfig;
		return;
	}
	// if the resource is already in the config, replace it with '' and then place it in the desired position
	const currentIndex = newConfig.indexOf(resource);
	if (currentIndex !== -1) {
		newConfig[currentIndex] = "";
	}
	// if the the place is occupied by "", we dont need to shift anything, just place the resource there
	if (newConfig[orderNumber - 1] === "") {
		newConfig[orderNumber - 1] = resource;
		tempResourcePanelConfig.value = newConfig;
		return;
	}
	// if the place is occupied by another resource, we need to shift the others to the right, but if inserting to higher index, we need to switch to left
	if (currentIndex < orderNumber - 1) {
		newConfig.splice(currentIndex, 1);
		newConfig.splice(orderNumber - 1, 0, resource);
	} else {
		newConfig.splice(currentIndex, 1);
		newConfig.splice(orderNumber - 1, 0, resource);
	}
	tempResourcePanelConfig.value = newConfig;
};

const isResourceInThisOrderClass = (
	resource: ResourceType | "",
	orderNumber: number,
): string => {
	if (tempResourcePanelConfig.value[orderNumber - 1] === resource) {
		return "current-order";
	}
	return "";
};

const isResourceInSelected = (resource: ResourceType | ""): boolean => {
	return tempResourcePanelConfig.value.includes(resource);
};

const isButtonDisabled = (): boolean => {
	// if tempResourcePanelConfig has any empty string, disable the button
	return tempResourcePanelConfig.value.includes("");
};

const saveConfig = (): void => {
	// add nonselected resources to the end of the config
	const finalConfig = [...tempResourcePanelConfig.value];
	for (const resource of DEFAULT_RESOURCE_PANEL_CONFIG) {
		if (!finalConfig.includes(resource)) {
			finalConfig.push(resource);
		}
	}
	useUserStore().setResourcePanelConfig(finalConfig);

	usePopupStore().hidePopup();
};
</script>

<style lang="scss" scoped>
.resource-panel-configure-popup {
	display: flex;
	flex: 1;
	flex-direction: column;
	flex-wrap: wrap;
	justify-content: flex-start;
	gap: 0.25rem;
	margin-top: 0.188rem;
	padding: 0.75rem 0;
	width: 100%;
	.resource-panel-preview {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.125rem;
		padding: 0 2rem;
		.resource-preview {
			display: flex;
			position: relative;
			flex: 1 1 30%;
			justify-content: space-between;
			align-items: center;
			border-top: 0.125rem solid #464550;
			border-right: 0.125rem solid #111015;
			border-bottom: 0.125rem solid #111015;
			border-left: 0.125rem solid #464550;
			background-color: #2e2d39;
			width: 100%;
			height: 1.563rem;
			height: 2rem;

			&:nth-child(-n + 3) {
				flex-basis: 30%; /* first 3 items evenly spaced */
			}

			&:nth-child(n + 4) {
				flex-basis: 25%; /* remaining 4 per row */
				margin-top: 0.188rem;
			}

			.resource-order-number {
				box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
				border-width: 0.075rem;
				border-style: solid;
				border-image-source: linear-gradient(
					to top,
					rgb(189, 144, 50),
					rgb(240, 211, 134)
				);
				border-image-slice: 1;
				background-image: linear-gradient(
					to top,
					rgb(135, 88, 6),
					rgb(221, 154, 39)
				);
				width: 1.75rem;
				height: 1.75rem;
				color: rgb(255, 243, 221);
				font-weight: 800;
				font-size: 1.0625rem;
				line-height: 1.3;
				text-shadow: 0.0437rem 0.0437rem 0 rgba(0, 0, 0, 0.6);
			}
			.resource-icon-wrapper {
				flex: 1;
				@include flex-row-center;
				.question-mark {
					color: rgb(255, 61, 61);
					font-weight: 800;
					font-size: 1.0625rem;
					text-shadow: 0.0437rem 0.0437rem 0 rgba(0, 0, 0, 0.6);
				}
			}
		}
	}

	:deep(.scroll-fade-container) {
		width: unset;
		height: 14rem;
	}
	.resource-panel-option-header {
		margin: 0 auto;
		color: rgb(55, 38, 21);
		font-size: 0.9063rem;
	}
	.resource-panel-options {
		@include flex-col-center;
		gap: 0.25rem;
		padding: 0 1.25rem;
	}
	.resource-panel-option-line {
		@include flex-row-between;
		border-top: 0.125rem solid #464550;
		border-right: 0.125rem solid #111015;
		border-bottom: 0.125rem solid #111015;
		border-left: 0.125rem solid #464550;
		background: linear-gradient(
			90deg,
			rgba(145, 19, 49, 1) 0%,
			rgba(46, 45, 57, 1) 50%
		);
		width: 100%;
		height: 2.5rem;

		&.selected {
			background: linear-gradient(
				90deg,
				rgba(25, 125, 54, 1) 0%,
				rgba(46, 45, 57, 1) 50%
			);
		}
		&__label {
			width: 8.75rem;
			@include flex-row-between;
			gap: 0.25rem;
			padding: 0 0.5rem;
			.label-text {
				justify-content: flex-start;
				width: 4rem;
				color: rgb(255, 243, 221);
				font-weight: 800;
				font-size: 0.8125rem;
				text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
			}
			.icon-check {
				width: 1rem;
				height: 1rem;
			}
			.icon-red-x {
				width: 1rem;
				height: 1rem;
			}
		}

		&__options {
			@include flex-row-center;
			flex-grow: 1;
			gap: 0.375rem;

			&__option {
				cursor: pointer;
				box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
				border-width: 0.075rem;
				border-style: solid;
				border-radius: 50%;
				border-image-slice: 1;
				background-image: linear-gradient(
					to top,
					rgb(24, 26, 29),
					rgb(48, 50, 57)
				);
				width: 1.75rem;
				height: 1.75rem;
				color: rgb(244, 210, 120);
				font-weight: 800;
				font-size: 1.0625rem;
				line-height: 1.375rem;
				text-shadow: 0.0437rem 0.0437rem 0 rgba(0, 0, 0, 0.6);

				&.current-order {
					background-image: linear-gradient(
						to right,
						rgb(135, 88, 6),
						rgb(221, 154, 39)
					);
				}
			}
		}
	}

	.error-message {
		margin: 0 auto;
		height: 1rem;
		color: rgb(207, 0, 50);
		font-size: 0.6875rem;
	}
	.text-button {
		margin: 0.125rem auto;
	}

	:deep(.horizontal-decor-divider) {
		margin: 0 auto;
		width: 23.75rem;
	}
	.border-like {
		margin-top: -0.5rem;
	}
}
</style>
